import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import moment from "jalali-moment";
import { Button, Spin } from "antd";
import Map from "@/components/map/Map";
import { Service } from "@helpers/ui_helper";
import { getVisitReportData } from "@services/api";
import {
  academicDegreeDetector,
  calculateArea,
  relativeToTheTotalArea,
} from "@/components/reports/soil/soilMapHelper";
import SoilMap from "@/components/reports/soil/soil-map/SoilMap";
import { hejriDate } from "@helpers/financial_helper";
import {
  expertSuggestionProposition1UiHelper,
  expertSuggestionProposition2UiHelper,
  expertSuggestionProposition3UiHelper,
  isShowExpertSuggestionProposition1UiHelper,
  isShowExpertSuggestionProposition2UiHelper,
  isShowExpertSuggestionProposition3UiHelper,
  isShowProposition2UiHelper,
  proposition1UiHelper,
  proposition2UiHelper,
} from "@/components/reports/visit/visitReportUiHelper";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import AgroMapFarm from "@/features/map/presentation/components/AgroMapFarm";
import line from "@assets/images/visit_report_images/Line185.png";
import visitLogo from "@assets/images/visit_report_images/visit-logo.png";
import footerImage from "@assets/images/visit_report_images/footer-page.png";
import headerImage from "@assets/images/visit_report_images/header-cover.png";
import frame2182 from "@assets/images/visit_report_images/header-page-visit.png";
import footerIntropPage from "@assets/images/visit_report_images/footer-intro-page.png";
import agroSoilMapIcon from "@assets/images/leaf_map_report_images/agroyaarSoilMapReport.svg";

function VisitReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { returnTo } = location.state;

  const [farmerInformation, setFarmerInformation] = useState({});
  const [seasonInformation, setSeasonInformation] = useState({});
  const [farmInformation, setFarmInformation] = useState({});
  const [expertInformation, setExpertInformation] = useState(null);
  const [visitStationQuestionsData, setVisitStationQuestionsData] = useState(
    []
  );
  let pageNumber = 0;

  const visitReportQuery = useQuery({
    queryKey: ["visitReportQuery", id],
    queryFn: () => getVisitReportData(id),
  });

  useEffect(() => {
    if (visitReportQuery?.data) {
      setFarmerInformation(visitReportQuery?.data?.visit?.farmer);
      setExpertInformation(visitReportQuery?.data?.visit?.expert);
      setSeasonInformation(visitReportQuery?.data?.visit?.season);
      setFarmInformation(visitReportQuery?.data?.visit?.season?.farm);
    }
  }, [visitReportQuery?.data]);

  useEffect(() => {
    if (visitReportQuery?.data) {
      visitReportQuery?.data?.visit?.parametersMaps?.forEach(
        (parameterMapInfo) => {
          visitReportQuery?.data?.visit?.data?.plots[0]?.stations[0]?.stationQuestions?.forEach(
            (question) =>
              parameterMapInfo?.parameterId === question?.questionCode &&
              setVisitStationQuestionsData((prev) => {
                return [
                  ...prev,
                  { mapInfo: parameterMapInfo, stationQuestion: question },
                ];
              })
          );
        }
      );
    }
  }, [visitReportQuery?.data]);

  let numberOf6 = visitReportQuery?.data?.visit?.reportImages?.length / 6;
  let numberOfVisitImagesDynamicPages = [];
  let startOfStep = 0,
    endOfStep = 0;
  while (numberOf6 > 0) {
    numberOfVisitImagesDynamicPages.push({ sample: "testObject" });
    --numberOf6;
  }

  return (
    <>
      {visitReportQuery?.isLoading ? (
        <div className="flex flex-row items-center w-[500px] h-[500px] mx-auto my-[6rem]">
          <Spin
            className="spiner-loading-visit"
            style={{ margin: "auto" }}
            size="large"
          />
        </div>
      ) : (
        <div className="section-container flex flex-col items-center justify-center w-[720px] mx-auto !bg-white">
          {/* Page1 (Introduction) */}
          <div className="page1">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={headerImage}
                alt="header"
                width="720px"
              />

              <div className="flex flex-col justify-between items-center w-full mt-[-90px] leading-[9rem]">
                <p
                  className="text-[#222] text-[36px] font-bold !font-[B-Nazanin]"
                  style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)" }}
                >
                  کشاورزی{" "}
                  <span className="text-visit !font-[B-Nazanin]">
                    مطمئن‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر، </span>
                  </span>
                  <span className="text-visit !font-[B-Nazanin]">
                    دقیق‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر و </span>
                  </span>
                  <span className="text-visit !font-[B-Nazanin]">
                    اقتصادی‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر</span>
                  </span>
                </p>

                <img
                  src={visitLogo}
                  className="w-[220px] !font-[B-Nazanin]"
                  alt="visit-icon"
                />

                <span
                  className="text-visit font-bold text-[64px] !font-[B-Nazanin]"
                  style={{
                    textShadow: "1px 0px 3px rgba(118, 118, 118, 0.61)",
                  }}
                >
                  گزارش بازدید
                </span>

                <p className="text-[#000] font-semibold text-[24px] !font-[B-Nazanin]">
                  هدف ما تصمیم‌گیری برای کشاورز نیست، کمک به تصمیم‌گیری بهتر
                  کشاورز است
                </p>

                <p className="text-visit font-bold text-[28px] tracking-[4px]">
                  WWW.AGROYAAR.COM
                </p>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerIntropPage}
                  width="720px"
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[35px] tracking-[7px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                  WWW.AGROYAAR.COM
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          {/* Page2 (Description) */}
          <div className="page2">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header"
                  width="720px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-row justify-start items-center w-full gap-5 mt-[-30px] px-4">
                <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                    اطلاعات کشاورز
                  </span>

                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <span className="!font-[B-Nazanin]">
                        {farmerInformation?.firstName}{" "}
                        {farmerInformation?.lastName}
                      </span>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">کد کشاورزی:</span>
                        <span className="!font-sans text-[16px]">
                          {farmerInformation?.farmerCode}
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">استان و شهر:</span>
                        <span className="!font-[B-Nazanin]">
                          {farmerInformation?.province}،{" "}
                          {farmerInformation?.city}
                        </span>
                      </div>
                    </div>

                    <img
                      src={
                        process.env.REACT_APP_ARVAN_S3_BASE_URL +
                        farmerInformation?.avatar
                      }
                      alt="profile"
                      width="70px"
                      height="70px"
                      className="rounded-full border-[2px] border-solid border-visit"
                    />
                  </div>
                </div>

                {expertInformation && (
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات کارشناس
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <span className="!font-[B-Nazanin]">
                          {expertInformation?.firstName}{" "}
                          {expertInformation?.lastName}
                        </span>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            کد کارشناسی:
                          </span>
                          <span className="!font-sans text-[16px]">
                            {expertInformation?.expertCode}
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            استان و شهر:
                          </span>
                          <span className="!font-[B-Nazanin]">
                            {expertInformation?.province}،{" "}
                            {expertInformation?.city}
                          </span>
                        </div>
                      </div>

                      <img
                        src={
                          process.env.REACT_APP_ARVAN_S3_BASE_URL +
                          expertInformation?.avatar
                        }
                        alt="profile"
                        width="70px"
                        height="70px"
                        className="rounded-full border-[2px] border-solid border-visit"
                      />
                    </div>
                  </div>
                )}
              </div>

              <img
                src={line}
                alt="line"
                width="720px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col justify-start items-start w-full gap-5 px-4">
                <div className="flex flex-row items-center justify-center w-full gap-6">
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات زمین
                    </span>
                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">نام قطعه:</span>
                          <span className="!font-[B-Nazanin]">
                            {farmInformation?.name}
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]"> استان:</span>
                          <span className="!font-[B-Nazanin]">
                            {farmInformation?.province?.name}
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">شهر:</span>
                          <span className="!font-[B-Nazanin]">
                            {farmInformation?.city?.name}
                          </span>
                        </div>
                      </div>

                      <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                        <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                        <AgroMapFarm
                          mapId={farmInformation?.id}
                          type="onlyShow"
                          toolbarOptions={toolbarBaseControls}
                          markerIcon={FieldMarkerIcon}
                          currentFarmPolygon={farmInformation?.polygon}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin] items-start">
                      اطلاعات فصل رشد
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">نام محصول:</span>
                          <span className="!font-[B-Nazanin]">
                            {seasonInformation?.product?.persianName}
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">مساحت زمین</span>
                          <span className="!font-[B-Nazanin]">
                            {seasonInformation?.area} هکتار
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">فصل رشد:</span>
                          <span className="!font-[B-Nazanin]">
                            {moment(
                              `${seasonInformation?.growingSeason}-04-01`,
                              "YYYY-MM-DD"
                            )
                              .locale("fa")
                              .format("YYYY")}{" "}
                            {seasonInformation?.persianName
                              ? ` - ${seasonInformation?.persianName}`
                              : ""}
                          </span>
                        </div>
                      </div>

                      <img
                        src={seasonInformation?.product?.imageUrl}
                        alt="product-name-image"
                        width="80px"
                        height="80px"
                        className="rounded-full"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="720px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col items-start justify-center w-full leading-[2rem] px-4 gap-2">
                <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                  اطلاعات بازدید
                </span>

                <div className="flex flex-row items-center justify-start w-full gap-6">
                  <div className="flex flex-col text-agrotext font-bold text-[18px] justify-start items-start">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">نوع بازدید:</span>
                      <span className="!font-[B-Nazanin]">
                        {visitReportQuery?.data?.visit?.type?.name}
                      </span>
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تعداد ایستگاه بازدید شده:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {
                          visitReportQuery?.data?.visit?.data?.plots[0]
                            ?.stations?.length
                        }
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col text-agrotext font-bold text-[18px] justify-start items-start">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تاریخ درخواست بازدید:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(visitReportQuery?.data?.visit?.createdAt)}
                      </span>
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تاریخ انجام بازدید:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(visitReportQuery?.data?.visit?.fulfilmentAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="720px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col justify-center items-start w-full gap-1 mb-[5.5rem] px-4">
                <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                  ایستگاه‌های بازدید شده
                </span>

                <div className="w-[680px] h-[330px] rounded-lg bg-slate-200">
                  <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-[680px] h-[330px] absolute"></div>

                  <Map
                    service={Service.FieldView}
                    polygonCoordinates={farmInformation?.polygon}
                    index="15"
                    productId={farmInformation?.productId}
                    samplingStationPoints={
                      visitReportQuery?.data?.visit?.data?.plots[0]?.stations
                    }
                    serviceReport={"VISIT"}
                  />
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[35px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                  کد بازدید: {visitReportQuery?.data?.visit?.visitCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* Page3 (generating dynamic pages) */}
          {visitStationQuestionsData?.map((parameterMapInfo, i) => {
            return (
              <div key={i} className="page3">
                <div className="flex flex-col justify-between items-start w-full h-[1048px]">
                  <div className="header w-full relative">
                    <img
                      className="header"
                      src={frame2182}
                      alt="header-image"
                      width="720px"
                    />
                    <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                      WWW.AGROYAAR.COM
                    </span>
                  </div>

                  <div className="flex flex-col items-center justify-start w-full h-full gap-4 px-4">
                    {i === 0 && (
                      <div className="flex flex-col items-start justify-center w-full gap-1 mt-[-10px]">
                        <p className="font-bold text-[18px] text-right text-[#222] !font-[B-Nazanin]">
                          با سلام و احترام
                        </p>
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {farmerInformation?.firstName}{" "}
                          {farmerInformation?.lastName} عزیز، بازدید از زمین{" "}
                          {farmInformation?.product?.persianName} شما (قطعه{" "}
                          {farmInformation?.name}) توسط کارشناس به صورت حضوری
                          انجام شده و اطلاعات بازدید توسط تیم تحلیل بازدید مورد
                          بررسی قرار گرفت. در ادامه گزارش بازدید جهت بهره‌برداری
                          و اقدامات لازم، تقدیم حضور می‌گردد.
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col items-start justify-center w-full">
                      <p className="font-bold text-[20px] text-right text-visit !font-[B-Nazanin]">
                        نقشه {parameterMapInfo?.stationQuestion?.persianName}
                      </p>

                      <div className="flex flex-col items-center justify-center w-full border-[3px] border-solid bg-visit border-visit rounded-[10px]">
                        <div className="relative w-[685px] h-[438px] overflow-clip border-b-[3px] border-solid border-visit rounded-tr-[7px] rounded-tl-[7px]">
                          <div className="flex flex-row items-start justify-between w-[670px] absolute z-[9999] right-2 top-2">
                            <div className="bg-white !font-[B-Nazanin] font-bold rounded-[15px] min-w-[132px] text-sm py-1 text-center border-[1.5px] border-solid border-visit">
                              قطعه {farmInformation?.name}
                            </div>
                            <img
                              src={agroSoilMapIcon}
                              width={200}
                              height={30}
                              alt="agroSoilMapIcon"
                            />
                          </div>

                          <div className="THISISFORMAPLOCK! bg-transparent z-[999] w-full h-full absolute"></div>

                          <SoilMap
                            parameterMapInfo={parameterMapInfo}
                            polygonBounds={seasonInformation?.polygon}
                            mapId={parameterMapInfo?.mapInfo?.parameterId}
                          />
                        </div>

                        <div className="flex flex-row items-start rounded-br-[7px] rounded-bl-[7px] bg-gradient-to-l from-[#ffffffcc] to-white justify-between w-full px-2">
                          <div className="flex flex-col items-start">
                            <p className="!font-[B-Nazanin] font-bold">
                              کشاورز: {farmerInformation?.firstName}{" "}
                              {farmerInformation?.lastName}
                            </p>
                            <p className="!font-[B-Nazanin] font-bold">
                              کارشناس:{" "}
                              {academicDegreeDetector(
                                expertInformation?.academicDegreeId
                              )}{" "}
                              {expertInformation?.firstName}{" "}
                              {expertInformation?.lastName}
                            </p>
                            <p className="!font-[B-Nazanin] font-bold">
                              نام محصول: {farmInformation?.product?.persianName}
                            </p>
                            <p className="!font-[B-Nazanin] font-bold">
                              مساحت: {farmInformation?.area} هکتار
                            </p>
                            <p className="!font-[B-Nazanin] font-bold">
                              محل زمین: {farmInformation?.province},{" "}
                              {farmInformation?.city}
                            </p>
                          </div>

                          <table
                            dir="ltr"
                            style={{ borderCollapse: "collapse" }}
                          >
                            <thead>
                              <tr className="border-b-[1px] border-solid border-visit">
                                <th
                                  dir="rtl"
                                  className="w-[80px] !font-[B-Nazanin] text-[16px] text-center"
                                >
                                  محدوده
                                </th>
                                <th
                                  dir="rtl"
                                  className="w-[120p   x] !font-[B-Nazanin] text-[16px] text-center"
                                >
                                  {parameterMapInfo?.stationQuestion?.typeDesc.trim() ===
                                    "عملکرد" ||
                                  parameterMapInfo?.stationQuestion?.typeDesc.trim() ===
                                    "اگرونومیک"
                                    ? "مقدار"
                                    : "مقدار (%)"}
                                </th>
                                <th className="w-[80px] !font-[B-Nazanin] text-[16px] text-center">
                                  مساحت (هکتار)
                                </th>
                                <th
                                  dir="rtl"
                                  className="w-[150px] !font-[B-Nazanin] text-[16px] text-center"
                                >
                                  نسبت به کل مساحت (%)
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {parameterMapInfo?.mapInfo?.ranges?.map(
                                (range, i) => {
                                  return (
                                    <tr
                                      className="border-b-[1px] border-solid border-visit"
                                      key={i}
                                    >
                                      <td className="w-[80px] text-center !font-[B-Nazanin] text-[16px] px-2 font-medium">
                                        <div className="flex flex-row items-center justify-center gap-1">
                                          <span>{range?.label}</span>
                                          <div
                                            style={{
                                              backgroundColor: `rgba(${range?.color[0]},${range?.color[1]},${range?.color[2]},${range?.color[3]})`,
                                              width: "50px",
                                              height: "20px",
                                            }}
                                          ></div>
                                        </div>
                                      </td>
                                      <td className="w-[120px] text-center !font-[B-Nazanin] text-[17px] px-2 font-medium">
                                        {range?.min} - {range?.max}
                                      </td>
                                      <td className="w-[80px] text-center !font-[B-Nazanin] text-[17px] px-2 font-medium">
                                        {calculateArea(
                                          parameterMapInfo?.mapInfo?.percentage[
                                            i
                                          ],
                                          farmInformation?.area
                                        ) === 0
                                          ? "≈0"
                                          : calculateArea(
                                              parameterMapInfo?.mapInfo
                                                ?.percentage[i],
                                              farmInformation?.area
                                            )}
                                      </td>
                                      <td className="w-[150px] text-center !font-[B-Nazanin] text-[17px] px-2 font-medium">
                                        {relativeToTheTotalArea(
                                          parameterMapInfo?.mapInfo?.percentage[
                                            i
                                          ],
                                          parameterMapInfo?.mapInfo
                                        )}
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    {parameterMapInfo?.stationQuestion?.typeDesc.trim() !==
                      "عملکرد" && (
                      <div className="flex flex-col items-start justify-start w-full">
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {proposition1UiHelper(
                            parameterMapInfo?.stationQuestion?.persianName,
                            parameterMapInfo
                          )}
                        </p>
                        {isShowProposition2UiHelper(parameterMapInfo) && (
                          <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                            {proposition2UiHelper(
                              parameterMapInfo?.stationQuestion?.persianName,
                              parameterMapInfo
                            )}
                          </p>
                        )}
                        {/* {
                                                        isShowProposition3UiHelper(parameterMapInfo) &&
                                                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                                                            {proposition3UiHelper(parameterMapInfo?.stationQuestion?.persianName, farmInformation?.area, parameterMapInfo)}
                                                        </p>
                                                    } */}

                        {parameterMapInfo?.mapInfo?.description && (
                          <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                            {parameterMapInfo?.mapInfo?.description}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
                    <img
                      className="absolute bottom-0 z-[2]"
                      src={footerImage}
                      width="750px"
                      alt="footer"
                    />
                    <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                      کد بازدید: {visitReportQuery?.data?.visit?.visitCode}
                    </span>
                    <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                      {++pageNumber}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* visit images page */}
          {numberOfVisitImagesDynamicPages.map((page, i) => {
            startOfStep = endOfStep;
            if (
              endOfStep + 6 >
              visitReportQuery?.data?.visit?.reportImages?.length
            ) {
              endOfStep = visitReportQuery?.data?.visit?.reportImages?.length;
            } else {
              endOfStep += 6;
            }
            return (
              <div key={i} className="visit-images-page">
                <section className="flex flex-col justify-between items-center w-full h-[1048px]">
                  <div className="header w-full relative">
                    <img
                      className="header"
                      src={frame2182}
                      alt="header"
                      width="720px"
                    />
                    <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                      WWW.AGROYAAR.COM
                    </span>
                  </div>

                  <div className="flex flex-col items-start justify-start gap-2 w-full h-full px-4">
                    {i === 0 && (
                      <p className="font-bold text-[20px] text-right text-[#222] !font-[B-Nazanin] mt-[-12px]">
                        تصاویر بازدید
                      </p>
                    )}
                    <div className="flex flex-wrap items-center justify-center w-full gap-x-7 gap-y-4">
                      {visitReportQuery?.data?.visit?.reportImages
                        ?.slice(startOfStep, endOfStep)
                        ?.map((imageUrl, i) => {
                          return (
                            <img
                              className="w-[270px] h-[240px] rounded-[5px] border-[2px] border-solid border-visit"
                              key={i}
                              src={
                                process.env.REACT_APP_ARVAN_S3_BASE_URL +
                                imageUrl
                              }
                              alt="visit-image"
                            />
                          );
                        })}
                    </div>
                  </div>

                  <div className="footer w-full flex flex-col justify-start items-center relative">
                    <img
                      className="absolute bottom-0 z-[2]"
                      src={footerImage}
                      width="750px"
                      alt="footer"
                    />
                    <span className="absolute bottom-[5px] left-[35px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                      کد بازدید: {visitReportQuery?.data?.visit?.visitCode}
                    </span>
                    <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                      {++pageNumber}
                    </span>
                  </div>
                </section>
              </div>
            );
          })}

          {/* last page (expert suggestion content) */}
          {/* visitStationQuestionsData?[0].stationQuestion?.typeDesc.trim() !== 'عملکرد' && */}

          {visitStationQuestionsData
            ? [0].stationQuestion?.typeDesc.trim() !== "عملکرد" && (
                <div className="last-page">
                  <section className="flex flex-col justify-between items-center w-full h-[1048px]">
                    <div className="header w-full relative">
                      <img
                        className="header"
                        src={frame2182}
                        alt="header"
                        width="720px"
                      />
                      <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                        WWW.AGROYAAR.COM
                      </span>
                    </div>

                    <div className="flex flex-col items-start justify-start gap-8 w-full h-full px-4">
                      <p className="font-bold text-[20px] text-right text-[#222] !font-[B-Nazanin]">
                        توصیه کارشناسی
                      </p>

                      {isShowExpertSuggestionProposition1UiHelper(
                        visitStationQuestionsData
                      ) && (
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {expertSuggestionProposition1UiHelper(
                            visitStationQuestionsData
                          )}
                        </p>
                      )}
                      {isShowExpertSuggestionProposition2UiHelper(
                        visitStationQuestionsData
                      ) && (
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {expertSuggestionProposition2UiHelper(
                            visitStationQuestionsData
                          )}
                        </p>
                      )}
                      {isShowExpertSuggestionProposition3UiHelper(
                        visitStationQuestionsData
                      ) && (
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {expertSuggestionProposition3UiHelper(
                            visitStationQuestionsData
                          )}
                        </p>
                      )}
                      {visitReportQuery?.data?.visit?.reportDescription && (
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {visitReportQuery?.data?.visit?.reportDescription}
                        </p>
                      )}
                    </div>

                    <div className="footer w-full flex flex-col justify-start items-center relative">
                      <img
                        className="absolute bottom-0 z-[2]"
                        src={footerImage}
                        width="750px"
                        alt="footer"
                      />
                      <span className="absolute bottom-[5px] left-[35px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                        کد بازدید: {visitReportQuery?.data?.visit?.visitCode}
                      </span>
                      <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                        {++pageNumber}
                      </span>
                    </div>
                  </section>
                </div>
              )
            : null}

          <div className="flex flex-col">
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[20px] right-[100px] max-lg:right-[32px] px-8 text-[18px]  w-[150px]"
              id="download-button"
              onClick={() => window.print()}
            >
              <p>پرینت گزارش</p>
            </Button>
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[65px] right-[100px] max-lg:right-[32px] px-8 text-[18px] w-[150px]"
              id="download-button"
              onClick={() => navigate(returnTo)}
            >
              <p> بازگشت</p>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default VisitReport;
